import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, Loader2 } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { useBackupBridge } from '@/src/renderer/lib/backup/backupBridge'
import { allSelectableKeys, buildSelectionTree, carrySelection, selectRows, selectsEverything } from '@/src/renderer/lib/backup/selection'
import type { BackupSnapshot } from '@/src/renderer/lib/backup/format'
import { buildCsvFiles } from '@/src/renderer/lib/backup/exportCsv'
import { buildMarkdownFiles } from '@/src/renderer/lib/backup/exportMarkdown'
import { exportFileName, zipExport } from '@/src/renderer/lib/backup/exportText'
import { SettingsSubpage } from '../../primitives'
import { BackupSelectionTree } from './BackupSelectionTree'
import './BackupViews.scss'

export type ExportFormat = 'csv' | 'markdown'

/** In Markdown gibt es keine Einstellungen und kein Profil — ausgegraut, mit Hinweis. */
const MARKDOWN_LOCKED = ['settings', 'profile'] as const

const treeOf = (snapshot: BackupSnapshot) => buildSelectionTree(snapshot.tables, { profile: true, hasProfile: !!snapshot.profile })

interface ExportViewProps {
  format: ExportFormat
  onBack: () => void
  /** Gespeichert — die Übersicht meldet es (das Einstellungsfenster hat keine Toasts). */
  onExported: (fileName: string) => void
}

/**
 * **Import & Export › CSV exportieren / Markdown exportieren** (#252): derselbe Baum wie beim Backup —
 * Lebensbereiche, Projekte, „Kurz notiert“, Ziele, Gewohnheiten, Einstellungen, Profil → Button →
 * Speichern-Dialog → zurück zur Übersicht. Für andere Apps, nicht wieder einlesbar.
 *
 * CSV nimmt Erledigtes immer mit (Spalte „Status“); Markdown nur mit dem Haken „Erledigte mitnehmen“,
 * standardmäßig aus (Entscheidung 18).
 */
export function ExportView({ format, onBack, onExported }: ExportViewProps) {
  const { t, i18n } = useTranslation()
  const dataSource = useDataSource()
  const account = useAccountSource()
  const bridge = useBackupBridge()
  const [source, setSource] = useState<{ snapshot: BackupSnapshot; accountId: string | null } | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [includeCompleted, setIncludeCompleted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [empty, setEmpty] = useState(false)
  // Schaltet das Menü während des Speichern-Dialogs um, darf der fertige Export die neue Unterseite nicht schließen.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const markdown = format === 'markdown'
  const tree = useMemo(() => (source ? treeOf(source.snapshot) : []), [source])
  const lockedKeys = useMemo(
    () => (markdown ? new Map<string, string>(MARKDOWN_LOCKED.map(key => [key, t('preferences.export.notInMarkdown')])) : undefined),
    [markdown, t],
  )

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const accountId = await account.getUserId().catch(() => null)
      const snapshot = await dataSource.readBackupRows({ accountId })
      if (cancelled) return
      setSource({ snapshot, accountId })
      setSelected(allSelectableKeys(treeOf(snapshot)))
    })()
    return () => {
      cancelled = true
    }
  }, [account, dataSource])

  const chosen = (keys: ReadonlySet<string>) => (markdown ? [...keys].filter(key => !(MARKDOWN_LOCKED as readonly string[]).includes(key)) : [...keys])
  const nothingChosen = chosen(selected).length === 0

  const onExport = async () => {
    if (!source || busy) return
    setBusy(true)
    setFailed(false)
    setEmpty(false)
    try {
      // Frisch lesen: Die Unterseite kann lange offen gestanden haben, das Hauptfenster schreibt weiter.
      const snapshot = await dataSource.readBackupRows({ accountId: source.accountId })
      const freshTree = treeOf(snapshot)
      const keys = new Set(chosen(carrySelection(tree, selected, freshTree)))
      const tables = selectRows(snapshot.tables, keys, { allTags: selectsEverything(freshTree, keys) })
      const files = markdown
        ? buildMarkdownFiles({ tables, includeCompleted, t })
        : buildCsvFiles({ tables, profile: keys.has('profile') ? snapshot.profile : null, language: i18n.language, t })
      // Markdown legt nur an, was offen ist: Ist alles Gewählte erledigt, entstünde eine leere ZIP-Datei.
      if (Object.keys(files).length === 0) {
        setEmpty(true)
        return
      }
      const name = exportFileName(format)
      const result = await bridge.saveAs(zipExport(files), name, format)
      if (result.saved && mounted.current) onExported(result.name ?? name)
    } catch (error) {
      console.error('[Export] Exportieren fehlgeschlagen:', error)
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const action = t(markdown ? 'preferences.export.markdownAction' : 'preferences.export.csvAction')

  return (
    <SettingsSubpage
      pageTitle={t('settings.subNav.import')}
      title={t(markdown ? 'preferences.export.markdownTitle' : 'preferences.export.csvTitle')}
      onBack={onBack}
      locked={busy}
    >
      <p className="backup-views__intro">{t(markdown ? 'preferences.export.markdownIntro' : 'preferences.export.csvIntro')}</p>
      {source ? (
        <>
          <BackupSelectionTree tree={tree} value={selected} onValueChange={setSelected} disabled={busy} lockedKeys={lockedKeys} />
          {markdown && (
            <label className="backup-views__option" htmlFor="export-include-completed">
              <input
                id="export-include-completed"
                type="checkbox"
                className="backup-views__checkbox"
                checked={includeCompleted}
                onChange={event => setIncludeCompleted(event.target.checked)}
                disabled={busy}
              />
              {t('preferences.export.includeCompleted')}
            </label>
          )}
          <div className="backup-views__actions">
            <Button
              variant="primary"
              disabled={busy || nothingChosen}
              icon={busy ? <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" /> : undefined}
              onClick={() => void onExport()}
            >
              {action}
            </Button>
          </div>
          {empty && (
            <p className="backup-views__result" role="status">
              {t('preferences.export.empty')}
            </p>
          )}
          {failed && (
            <p className="backup-views__result backup-views__result--failed" role="alert">
              <CircleAlert size={16} aria-hidden="true" />
              {t('preferences.export.failed')}
            </p>
          )}
        </>
      ) : (
        <p className="backup-views__loading">
          <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" />
          {t('preferences.backups.loading')}
        </p>
      )}
    </SettingsSubpage>
  )
}
